import './DistroHistoryChart.scss'
import {useMemo, useState} from 'react'
import {useSelector} from 'react-redux'
import {Chart} from 'react-chartjs-2'
import {
    assetTypeToGroup,
    getAllAssets,
    FILTER_KEY_CRYPTO,
    FILTER_KEY_FUNDS,
    FILTER_KEY_REAL_ESTATE,
    FILTER_KEY_STOCKS,
    FILTER_KEY_STOCKS_UNLISTED,
} from '../../ducks/assets-duck.js'
import {getHistoryArray} from '../../ducks/history-duck.js'
import {trimArrayEvenly} from '../../util/misc.js'
import {DONUT_STANDARD_COLORS, hexToRgb} from '../util/donut/_colors.js'

const DURATIONS = [
    {days: 31, label: '1m'},
    {days: 92, label: '3m'},
    {days: 183, label: '6m', hideOnSmall: true},
    {days: 365, label: '1y'},
    {days: 1096, label: '3y', hideOnSmall: true},
    {days: 1826, label: '5y'},
    {days: 3652, label: 'All'},
]

const GROUPS = [
    FILTER_KEY_REAL_ESTATE,
    FILTER_KEY_STOCKS_UNLISTED,
    FILTER_KEY_FUNDS,
    FILTER_KEY_STOCKS,
    FILTER_KEY_CRYPTO,
]

const GROUP_LABELS = {
    [FILTER_KEY_CRYPTO]: 'Crypto',
    [FILTER_KEY_STOCKS]: 'Stocks',
    [FILTER_KEY_FUNDS]: 'Funds',
    [FILTER_KEY_STOCKS_UNLISTED]: 'Unlisted',
    [FILTER_KEY_REAL_ESTATE]: 'Real estate',
}

const MAX_POINTS = 300

const BASE_OPTIONS = {
    interaction: {mode: 'index', axis: 'x', intersect: false},
    plugins: {
        legend: {
            display: false,
            position: 'bottom',
            labels: {color: 'rgba(255,255,255,0.7)', boxWidth: 12, padding: 16, font: {size: 12}},
        },
        tooltip: {enabled: true},
        annotation: {annotations: {}},
        eventPlugin: {onMouseLeave: () => {}, onMouseEnter: () => {}, onClick: () => {}},
    },
    scales: {
        x: {
            type: 'time',
            time: {round: 'date', displayFormats: {month: 'MMM YY', day: 'D MMM'}},
            grid: {display: false},
            border: {display: false},
            ticks: {color: 'rgba(255,255,255,0.4)', maxRotation: 0, maxTicksLimit: 6},
        },
    },
}

export default () => {
    const [duration, setDuration] = useState(365)
    const [isRelative, setIsRelative] = useState(true)
    const allAssets = useSelector(getAllAssets)
    const historyArray = useSelector(getHistoryArray)

    const assetGroupMap = useMemo(
        () => Object.fromEntries(allAssets.map(a => [String(a.id), assetTypeToGroup(a.asset_type)])),
        [allAssets]
    )

    const {labels, datasets} = useMemo(() => {
        const sliced = trimArrayEvenly(historyArray.slice(-(duration + 1)), MAX_POINTS)

        let startIdx = 0
        for (let i = 0; i < sliced.length; i++) {
            const total = Object.values(sliced[i][1]).reduce((s, a) => s + (a.value || 0), 0)
            if (total > 0) {startIdx = i; break}
        }
        const meaningful = sliced.slice(startIdx)

        const labels = meaningful.map(([date]) => date).reverse()
        const groupValues = Object.fromEntries(GROUPS.map(g => [g, []]))

        let finalTotals = Object.fromEntries(GROUPS.map(g => [g, 0]))
        for (const [, assets] of meaningful) {
            const totals = Object.fromEntries(GROUPS.map(g => [g, 0]))
            for (const [assetId, assetData] of Object.entries(assets)) {
                const group = assetGroupMap[assetId]
                if (group !== undefined && totals[group] !== undefined) {
                    totals[group] += assetData.value || 0
                }
            }
            for (const g of GROUPS) groupValues[g].push(totals[g])
            finalTotals = totals
        }
        for (const g of GROUPS) groupValues[g].reverse()

        if (isRelative) {
            const dayCount = labels.length
            for (let i = 0; i < dayCount; i++) {
                const dayTotal = GROUPS.reduce((sum, g) => sum + (groupValues[g][i] || 0), 0)
                if (dayTotal > 0) {
                    for (const g of GROUPS) groupValues[g][i] = (groupValues[g][i] / dayTotal) * 100
                }
            }
        }

        const groupsSortedByFinalValue = [...GROUPS].sort((a, b) => finalTotals[b] - finalTotals[a])
        const groupColorMap = Object.fromEntries(
            groupsSortedByFinalValue.map((g, i) => [g, DONUT_STANDARD_COLORS[i][0]])
        )

        const datasets = groupsSortedByFinalValue
            .filter(g => groupValues[g].some(v => v > 0))
            .map(g => {
                const lineColor = groupColorMap[g]
                const {r, b, g: green} = hexToRgb(lineColor)
                return {
                    label: GROUP_LABELS[g],
                    data: groupValues[g],
                    backgroundColor: `rgba(${r},${green},${b},0.1)`,
                    borderColor: lineColor,
                    borderWidth: 2,
                    pointRadius: 0,
                    fill: true,
                    tension: 0.3,
                }
            })

        return {labels, datasets}
    }, [historyArray, assetGroupMap, duration, isRelative])

    const chartOptions = useMemo(() => ({
        ...BASE_OPTIONS,
        scales: {
            ...BASE_OPTIONS.scales,
            y: {
                stacked: true,
                grid: {display: false},
                border: {display: false},
                min: isRelative ? 0 : undefined,
                max: isRelative ? 100 : undefined,
                ticks: {
                    display: isRelative,
                    color: 'rgba(255,255,255,0.4)',
                    callback: v => `${Math.round(v)}%`,
                },
            },
        },
    }), [isRelative])

    return (
        <div className='distro-history-chart'>
            <div className='chart-header'>
                <h3 className='hide-portrait'>Distribution over time</h3>
                <div className='controls'>
                    <div className='mode-picker'>
                        <button
                            className={!isRelative ? 'is-selected' : ''}
                            onClick={() => setIsRelative(false)}
                        >
                            NOK
                        </button>
                        <button
                            className={isRelative ? 'is-selected' : ''}
                            onClick={() => setIsRelative(true)}
                        >
                            %
                        </button>
                    </div>
                    <div className='duration-picker'>
                        {DURATIONS.map(d => (
                            <button
                                key={d.days}
                                className={`${duration === d.days ? 'is-selected' : ''} ${d.hideOnSmall ? 'hide-portrait' : ''}`}
                                onClick={() => setDuration(d.days)}
                            >
                                {d.label}
                            </button>
                        ))}
                    </div>
                </div>
            </div>
            <Chart type='line' data={{labels, datasets}} options={chartOptions}/>
        </div>
    )
}
